import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as NewAccountActions from './new-account.actions';
import { Store } from '@ngrx/store';
import { catchError, concatMap, filter, map, withLatestFrom } from 'rxjs/operators';
import { combineLatest, of } from 'rxjs';
import { globalFailure } from './../../../app/@store/common.actions';
import { getContractListConfig, getContractsMeta } from './../../../app/@store/new-account/new-account.selectors';
import { IMeta } from './../../../app/@store/request-options.interface';
import { PublicPartnerApiService } from './../../../app/@shared/services/publicpartnerapi.service';
import { loginSuccess } from '../auth/auth.actions';
import { ContractManagementApiService } from './../../../app/@shared/services/contractmanagementapi.service';
import { deleteAccountSuccess, patchAccountSuccess } from '../account/account.actions';
import { AccountsResponse } from './../../../app/@shared/models/accounts_response.model';
import * as _ from 'lodash';
import { HttpParams } from '@angular/common/http';

@Injectable()
export class NewAccountEffects {
  constructor(private readonly actions$: Actions, private readonly store: Store, private readonly api: PublicPartnerApiService, private readonly contractManagementApi: ContractManagementApiService) { }

  loadContracts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NewAccountActions.loadContracts, patchAccountSuccess, deleteAccountSuccess),
      filter(action => action.fromBatch !== true),
      withLatestFrom(this.store.select(getContractListConfig), this.store.select(getContractsMeta)),
      concatMap(
        ([action, config, _meta]) =>
          this.api.get<Partial<AccountsResponse>>('/partners/##CURRENT_PARTNER_UUID##/contracts', _.omitBy(config, c => c === undefined) as HttpParams).pipe(
            filter(result => result.contracts !== undefined),
            map((result) => {
              const total = result.contracts?.length ? (result.contracts![0].total_row_count || _meta.total || 0) : _meta.total || 0;

              const meta: IMeta = {
                count: result.contracts!.length,
                limit: _.get(config, 'limit', 10),
                page: 0,
                total
              };

              return NewAccountActions.loadContractsSuccess({
                data: result.contracts || [],
                meta
              });
            }),
            catchError(error => of(NewAccountActions.loadContractsFailure({ error }), globalFailure({ error })))
          )
      )
    )
  );

  loadContractsTotal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NewAccountActions.loadContractsTotal, loginSuccess, NewAccountActions.activateSuccess, patchAccountSuccess, deleteAccountSuccess),
      concatMap(action => {
        return this.api.get<Partial<AccountsResponse>>('/partners/##CURRENT_PARTNER_UUID##/contracts', { fields: ['object_uuid'], filter: ['payment_enabled=false'], limit: '1', page: '0' }).pipe(
          filter(result => result.contracts !== undefined),
          map((result) => {
            let total = 0;

            if (result.contracts?.length) {
              total = result.contracts![0]?.total_row_count || 0;
            }

            return NewAccountActions.loadContractsTotalSuccess({
              total
            });
          }),
          catchError(error => of(NewAccountActions.loadContractsTotalFailure({ error }), globalFailure({ error })))
        )
      })
    )
  );

  loadContractsNext$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NewAccountActions.loadContractsNext),
      withLatestFrom(combineLatest([this.store.select(getContractsMeta), this.store.select(getContractListConfig)])),
      concatMap(([action, [_meta, _listConfig]]) => {
        const nextPage = _.get(_meta, 'page', 0) + 1;
        const totalPages = Math.ceil(_.get(_meta, 'total', 0) / _.get(_meta, 'limit', 10));

        const config = { ..._.omitBy(_listConfig, c => c === undefined) };

        if (nextPage <= totalPages) {
          config.page = nextPage;
        }

        return this.api.get<Partial<AccountsResponse>>('/partners/##CURRENT_PARTNER_UUID##/contracts', config as HttpParams).pipe(
          filter(result => result.contracts !== undefined && result.contracts?.length > 0),
          map((result) => {
            const total = result.contracts![0]?.total_row_count || _meta.total || 0;

            const meta: IMeta = {
              count: result.contracts!.length,
              limit: config.limit || 10,
              page: config.page || 0,
              total
            };

            return NewAccountActions.loadContractsNextSuccess({
              data: result.contracts || [],
              meta
            });
          }),
          catchError(error => of(NewAccountActions.loadContractsNextFailure({ error }), globalFailure({ error })))
        )
      })
    )
  );


  activate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NewAccountActions.activate),
      concatMap(
        action =>
          this.contractManagementApi.patch<void>('/management/v2/contracts/' + action.account_uuid + '/activate').pipe(
            map(() => {
              return NewAccountActions.activateSuccess({
                account_uuid: action.account_uuid
              });
            }),
            catchError(error => of(NewAccountActions.activateFailure({ account_uuid: action.account_uuid }), globalFailure({ error })))
          )
      )
    )
  );

}
